/**
 * Telemetry Gap Detection & Fill Utility
 *
 * Detects offline/gap periods in hourly telemetry data and fills them
 * with gap markers so charts can show disconnection periods visually.
 *
 * A "gap" is defined as: two consecutive data points separated by more
 * than GAP_THRESHOLD_HOURS hours — meaning the device was offline/not
 * sending data during that interval.
 */

export interface SensorDataPoint {
  timestamp: Date;
  temperature?: number;
  humidity?: number;
  level?: number;
  battery?: number;
  rssi?: number;
  [key: string]: any;
  isGap?: boolean; // true = this slot has no data (offline period)
  gapDurationHours?: number; // how long the gap lasted
}

export interface GapPeriod {
  from: Date;
  to: Date;
  durationHours: number;
  durationLabel: string; // e.g. "3h 20m" or "2d 5h"
}

/** Any two consecutive points further apart than this = gap */
const GAP_THRESHOLD_HOURS = 1.5;

/**
 * Format a duration in hours into a human-readable string.
 * e.g. 1.5 → "1h 30m", 25.3 → "1d 1h"
 */
export function formatGapDuration(hours: number, isArabic: boolean): string {
  const totalMinutes = Math.round(hours * 60);
  const days = Math.floor(totalMinutes / (60 * 24));
  const remainingAfterDays = totalMinutes % (60 * 24);
  const hrs = Math.floor(remainingAfterDays / 60);
  const mins = remainingAfterDays % 60;

  if (isArabic) {
    if (days > 0) return `${days}ي ${hrs}س`;
    if (hrs > 0 && mins > 0) return `${hrs}س ${mins}د`;
    if (hrs > 0) return `${hrs}س`;
    return `${mins}د`;
  } else {
    if (days > 0) return `${days}d ${hrs}h`;
    if (hrs > 0 && mins > 0) return `${hrs}h ${mins}m`;
    if (hrs > 0) return `${hrs}h`;
    return `${mins}m`;
  }
}

/**
 * Detect gap periods between data points.
 * Returns list of GapPeriod objects.
 */
export function detectGaps(data: SensorDataPoint[], isArabic: boolean = false): GapPeriod[] {
  if (data.length < 2) return [];

  const gaps: GapPeriod[] = [];

  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1];
    const curr = data[i];
    const diffHours = (curr.timestamp.getTime() - prev.timestamp.getTime()) / (1000 * 60 * 60);

    if (diffHours > GAP_THRESHOLD_HOURS) {
      gaps.push({
        from: prev.timestamp,
        to: curr.timestamp,
        durationHours: diffHours,
        durationLabel: formatGapDuration(diffHours, isArabic),
      });
    }
  }

  return gaps;
}

/**
 * Fill gaps in telemetry data with gap-marker points.
 *
 * For each gap longer than GAP_THRESHOLD_HOURS:
 *   - Insert a null-value point at (gapStart + 1 hour) with isGap=true
 *   - Insert a null-value point at (gapEnd - 1 hour) with isGap=true
 *   This causes react-native-chart-kit to break the line at those points.
 *
 * The original valid data points are preserved unchanged.
 */
export function fillTimeGaps(data: SensorDataPoint[]): SensorDataPoint[] {
  if (data.length < 2) return data;

  const result: SensorDataPoint[] = [];

  for (let i = 0; i < data.length; i++) {
    result.push(data[i]);

    if (i < data.length - 1) {
      const curr = data[i];
      const next = data[i + 1];
      const diffHours = (next.timestamp.getTime() - curr.timestamp.getTime()) / (1000 * 60 * 60);

      if (diffHours > GAP_THRESHOLD_HOURS) {
        // Insert gap-start marker (one step after last real point)
        result.push({
          timestamp: new Date(curr.timestamp.getTime() + 60 * 60 * 1000),
          isGap: true,
          gapDurationHours: diffHours,
        });

        // Insert gap-end marker (one step before next real point)
        // Only if gap is wide enough to warrant two markers
        if (diffHours > 3) {
          result.push({
            timestamp: new Date(next.timestamp.getTime() - 60 * 60 * 1000),
            isGap: true,
            gapDurationHours: diffHours,
          });
        }
      }
    }
  }

  return result;
}
